import * as yup from "yup";

const supportedFormats = ["image/jpeg", "image/png", "application/pdf"];
const LICENSE_TYPE_ENUM = [
  "CL- 2 (Off Premise)",
  "CL- 4",
  "CL- 5",
  "CL- 6 + ARVB",
  "CL- 6A + ARVB",
  "CL- 7 + ARVB",
  "CL-9 + ARVB",
  "CL- 17 + ARVB",
  "CL- 18 + RVB",
];

// Reusable validation schemas
const pincodeSchema = () =>
  yup
    .string()
    .required("Pincode is required")
    .matches(
      /^[1-9][0-9]{5}$/,
      "Enter a valid 6-digit pincode. Pincode cannot start with 0."
    );

const alphanumericSchema = (fieldName: string) =>
  yup
    .string()
    .required(`${fieldName} is required`)
    .matches(/^[A-Za-z0-9]+$/, `Enter a valid ${fieldName}`);

const alphanumericOptionalSchema = (fieldName: string) =>
  yup.string().matches(/^[A-Za-z0-9]+$/, {
    message: `Enter a valid ${fieldName}`,
    excludeEmptyString: true,
  });

const numericSchema = (fieldName: string) =>
  yup
    .string()
    .required(`${fieldName} is required`)
    .matches(/^[0-9]+$/, `Enter a valid ${fieldName}`);

const numericOptionalSchema = (fieldName: string) =>
  yup.string().matches(/^[0-9]+$/, {
    message: `Enter a valid ${fieldName}`,
    excludeEmptyString: true,
  });

const phoneSchema = (fieldName: string, customMessage?: string) =>
  yup.string().matches(/^(91)?[6-9]\d{9}$/, {
    message:
      customMessage ??
      "Enter a valid 10-digit mobile number starting with 6\u20139.",
    excludeEmptyString: true,
  });

// Allows alphanumeric and special characters with no spaces
const specialCharString = (fieldname: string) =>
  yup
    .string()
    .matches(
      /^[A-Z0-9]{5,20}$/,
      "Enter a valid Customer Excise Registration Number (Example: EX29AB1234567)."
    )
    .required("Customer Excise Registration Number is required");

// Allows only alphabetic characters and spaces
const alphabeticString = (fieldname: string) =>
  yup.string().matches(/^[A-Za-z ]+$/, {
    message: `Please select a valid ${fieldname.toLowerCase()} from the dropdown list.`,
    excludeEmptyString: true,
  });

const gstSchema = (
  fieldName: string,
  required = false,
  customMessage?: string
) => {
  const errMsg =
    customMessage ??
    "Enter a valid 15-character GST number (Example: 27ABCDE1234F1Z5).";
  if (required) {
    return yup
      .string()
      .required(`${fieldName} is required`)
      .matches(
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
        errMsg
      );
  }
  return yup
    .string()
    .matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, {
      message: errMsg,
      excludeEmptyString: true,
    });
};

const panSchema = (fieldName: string, required = false) => {
  const base = yup
    .string()
    .matches(
      /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
      "Enter a valid PAN number in uppercase format (Example: ABCDE1234F)."
    );
  return required ? base.required(`${fieldName} is required`) : base.optional();
};

const nameOnlySchema = (
  fieldName: string,
  required = false,
  customMessage?: string
) => {
  if (required) {
    return yup
      .string()
      .required(`${fieldName} is required`)
      .min(2, `${fieldName} must be at least 2 characters`)
      .max(100, `${fieldName} cannot exceed 100 characters`)
      .matches(
        /^[a-zA-Z\s.'-]+$/,
        `${fieldName}: only letters, spaces, period (.) and apostrophe (') allowed`
      );
  }
  return yup.string().test("name-only-optional", "", function (value) {
    if (!value || value.trim() === "") return true;
    if (value.length < 2)
      return this.createError({
        message: customMessage ?? `${fieldName} must be at least 2 characters`,
      });
    if (value.length > 100)
      return this.createError({
        message: customMessage ?? `${fieldName} cannot exceed 100 characters`,
      });
    if (!/^[a-zA-Z\s.'-]+$/.test(value))
      return this.createError({
        message:
          customMessage ??
          `Enter a valid name (letters, spaces, and . ' only).`,
      });
    return true;
  });
};

// NOTE: for images, if its not mandatory, keep the default value as ""
const fileSchema = yup
  .mixed()
  .required("File is required")
  .test("required", `File is required`, (value) => {
    if (value == "") return true;
    return (
      value instanceof File ||
      (typeof value === "object" && value !== null && "url" in value)
    );
  })
  .test("fileType", `Unsupported File Format`, (value) => {
    if (value == "") return true;
    if (value instanceof File) {
      return supportedFormats.includes(value.type);
    }
    if (typeof value === "object" && value !== null && "url" in value) {
      return true;
    }
    return false;
  });

export const customerDetailsStep1 = yup.object({
  outletName: yup
    .string()
    .required("Outlet Name is required")
    .min(2, "Outlet Name must be at least 2 characters")
    .max(100, "Outlet Name cannot exceed 100 characters")
    .matches(/^[a-zA-Z0-9\s\-&.]+$/, "Enter a valid Outlet Name."),
  companyGST: gstSchema("Company GST Number", true),
  companyPAN: panSchema("Company PAN Number", true),
  registeredAddress: yup
    .string()
    .required("Registered Address is required")
    .min(10, "Registered Address must be at least 10 characters")
    .max(500, "Registered Address cannot exceed 500 characters"),
  authorizedSignatory: nameOnlySchema("Company Authorized Signatory"),
  vatNumber: gstSchema(
    "VAT Number",
    false,
    "Enter a valid VAT number (Example: 29ABCDE1234F1Z5)."
  ),
  state: yup.string().required("State is required"),
  pincode: pincodeSchema(),
  emailID: yup
    .string()
    .required("Email is required")
    .matches(
      /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
      "Enter a valid email address (Example: name@company.com)."
    ),
  gstStateCode: numericOptionalSchema("GST state code"),
  comp_gst_cert: fileSchema,
  comp_pan_card: fileSchema,
  comp_vat_cert: fileSchema,
  phoneNumber: phoneSchema("Phone number"),
});

export const customerDetailsStep2 = yup.object({
  licenseName: yup
    .string()
    .required("License Name is required")
    .min(2, "License Name must be at least 2 characters")
    .max(150, "License Name cannot exceed 150 characters")
    .matches(/^[a-zA-Z0-9\s\-&.]+$/, "Enter a valid License Name."),
  licenseType: yup
    .string()
    .oneOf(
      LICENSE_TYPE_ENUM,
      "Please select a valid license type from the dropdown list."
    )
    .required("License Type is required"),
  customerExciseRegistrationNumber: specialCharString(
    "Customer Excise Registration Number"
  ),
  licensePAN: panSchema("License PAN Number", true),
  licenseGST: gstSchema("License GST Number"),
  licenseAddress: yup
    .string()
    .test("license-address-optional", "", function (value) {
      if (!value || value.trim() === "") return true;
      if (value.length < 10)
        return this.createError({
          message: "Enter a complete address between 10–500 characters.",
        });
      if (value.length > 500)
        return this.createError({
          message: "Enter a complete address between 10–500 characters.",
        });
      return true;
    }),
  gstStateCode: numericOptionalSchema("GST state code"),
  lic_rvb_license: fileSchema,
  lic_license_pan: fileSchema,
  lic_gst_cert: fileSchema,
});

export const customerDetailsStep3 = yup.object({
  bankName: yup.string().test("bank-name-optional", "", function (value) {
    if (!value || value.trim() === "") return true;
    if (value.length < 2)
      return this.createError({
        message: "Bank Name must be at least 2 characters",
      });
    if (value.length > 100)
      return this.createError({
        message: "Bank Name cannot exceed 100 characters",
      });
    if (!/^[a-zA-Z0-9\s\-&.]+$/.test(value))
      return this.createError({ message: "Enter a valid Bank Name." });
    return true;
  }),
  bankBranch: yup.string().test("bank-branch-optional", "", function (value) {
    if (!value || value.trim() === "") return true;
    if (value.length < 2)
      return this.createError({
        message: "Bank Branch must be at least 2 characters",
      });
    if (value.length > 100)
      return this.createError({
        message: "Bank Branch cannot exceed 100 characters",
      });
    if (!/^[a-zA-Z0-9\s,.-]+$/.test(value))
      return this.createError({ message: "Enter a valid Bank Branch." });
    return true;
  }),
  accountType: alphabeticString("Account Type"),
  accountNumber: yup.string().matches(/^\d{9,18}$/, {
    message: "Enter a valid bank account number containing 9\u201318 digits.",
    excludeEmptyString: true,
  }),
  ifscCode: yup.string().matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, {
    message: "Enter a valid IFSC code (Example: MAHB0001821).",
    excludeEmptyString: true,
  }),
  beneficiaryName: yup
    .string()
    .test("beneficiary-optional", "", function (value) {
      if (!value || value.trim() === "") return true;
      if (value.length < 2)
        return this.createError({ message: "Enter a valid Beneficiary Name." });
      if (value.length > 100)
        return this.createError({ message: "Enter a valid Beneficiary Name." });
      if (!/^[a-zA-Z\s.'-]+$/.test(value))
        return this.createError({ message: "Enter a valid Beneficiary Name." });
      return true;
    }),
  accountsDepartmentPOCName: nameOnlySchema(
    "Accounts Dept POC Name",
    false,
    "Enter a valid Accounts Dept POC: Name."
  ),
  accountsDepartmentPOCNumber: phoneSchema(
    "Accounts Department POC Number",
    "Enter a valid 10-digit mobile number (Example: 9876543210)."
  ),
  bank_cancelled_cheque: fileSchema,
});
